import { useState } from "react";
import { useNavigate } from "react-router";
import { AskParentScreen } from "../../screens/payment/AskParentScreen";
import { useAsync } from "../../lib/useAsync";
import { fetchMe } from "../../api/me";
import { shareCheckoutWithParent } from "../../api/checkout";
import { ApiError } from "../../api/client";
import { ALL_ACCESS_MINOR, naira } from "../../lib/format";
import type { ShareChannel } from "../../lib/share";

/**
 * A preview of what the server will send, in the student's own numbers.
 *
 * Only a preview: the message that actually goes out is composed on the
 * server. Two copies of the same words is a risk, but the alternative —
 * letting the client supply the text — turns the endpoint into an open relay,
 * and a student who cannot see what goes out in their name will not send it.
 */
function previewMessage(input: {
  firstName: string | null;
  weakest: { name: string; readiness: number } | null;
}): string {
  const child = input.firstName?.trim() || "Your child";
  const evidence = input.weakest
    ? ` Their ${input.weakest.name} is at ${input.weakest.readiness}% and they want to raise it before the exam.`
    : "";

  return (
    `${child} is practising for JAMB/WAEC on PrepAI and asked you to unlock it.` +
    `${evidence} ${naira(ALL_ACCESS_MINOR)} once, no renewals — covers everything till their exam.`
  );
}

/**
 * Screen 13b — ask a parent to pay.
 *
 * The wall's second action navigated to `/p/pay`, the *parent's* page, which
 * needs a payment reference — so it fell through to the not-found screen. For
 * an under-18 this is the conversion path rather than a secondary one, and it
 * led nowhere.
 */
export function AskParentRoute() {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const me = useAsync(fetchMe);

  const measured = (me.data?.subjects ?? [])
    .filter(
      (subject): subject is typeof subject & { readiness: number } => subject.readiness !== null,
    )
    .sort((a, b) => a.readiness - b.readiness);

  const preview = me.data
    ? previewMessage({
        firstName: me.data.student.firstName,
        weakest: measured[0] ? { name: measured[0].name, readiness: measured[0].readiness } : null,
      })
    : undefined;

  const send = async (input: {
    channel: ShareChannel;
    contact: string;
  }): Promise<{ link: string } | void> => {
    if (sending) return;
    setSending(true);
    setFailure(null);

    try {
      const result = await shareCheckoutWithParent(input);
      setSentTo(result.sentTo);
      return { link: result.link };
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "We couldn't send that just now. Check the contact and try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <AskParentScreen
      {...(preview ? { preview } : {})}
      sending={sending}
      sentTo={sentTo}
      error={failure}
      onSend={send}
      onDone={() => void navigate("/practice")}
    />
  );
}
